import { useCallback, useMemo, useState, useEffect } from 'react'
import toast from 'react-hot-toast'

import {
  filterServices,
  services as seedServices,
  fleets as seedFleets,
  type ServiceCategoryFilter,
} from '../data/services'
import {
  apiGetServiceById,
  apiGetServices,
  apiToggleServiceStatus,
  type ServiceCategory,
} from '../api/services/servicesApi'
import {
  apiGetFleet,
  apiGetFleets,
  apiToggleFleetStatus,
} from '../api/services/fleetApi'
import { USE_DASHBOARD_REAL_BACKEND } from '../api/config/config'

/**
 * Owns the catalogue and the category filter for the Services page. Everything is in-memory:
 * swap `seedServices` and these mutations for API calls when a backend exists.
 */
export const useServices = () => {
  const [records, setRecords] = useState(USE_DASHBOARD_REAL_BACKEND ? [] : seedServices)
  const [fleetRecords, setFleetRecords] = useState(USE_DASHBOARD_REAL_BACKEND ? [] : seedFleets)
  const [category, setCategory] = useState<ServiceCategoryFilter>('day-trips')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    let mounted = true;
    const loadServices = async () => {
      setIsLoading(true);
      setError(null);
      
      if (!USE_DASHBOARD_REAL_BACKEND) {
        setRecords(seedServices);
        setFleetRecords(seedFleets);
        setIsLoading(false);
        return;
      }

      const [res, fleetsRes] = await Promise.all([apiGetServices(), apiGetFleets()]);
      if (!mounted) return;

      if (res.success && res.data) {
        setRecords(Array.isArray(res.data) ? res.data : [])
      } else {
        console.warn('API fetch failed.', res.message);
        setError(res.message);
        setRecords([]);
      }
      if (fleetsRes.success && fleetsRes.data) {
        setFleetRecords(fleetsRes.data)
      } else {
        console.warn('Fleet API fetch failed.', fleetsRes.message)
        setFleetRecords([])
      }
      setIsLoading(false);
    };
    loadServices();
    return () => { mounted = false; };
  }, []);

  const visible = useMemo(() => filterServices(records, category), [records, category])

  const toggleActive = useCallback(async (id: string) => {
    if (updatingIds.has(id)) return

    const service = records.find((record) => record.id === id)
    if (!service) return

    const active = !service.active
    setUpdatingIds((current) => new Set(current).add(id))

    try {
      if (!USE_DASHBOARD_REAL_BACKEND) {
        setRecords((current) =>
          current.map((record) =>
            record.id === id ? { ...record, active } : record,
          ),
        )
        return
      }

      const updated = await apiToggleServiceStatus(service.category as ServiceCategory, id, active)
      if (!updated.success) {
        toast.error(updated.message || 'Failed to update service status.')
        return
      }

      const refreshed = await apiGetServiceById(
        id,
        service.category as 'day-trips' | 'private-van-tours' | 'ski-transfers',
      )
      if (refreshed.success && refreshed.data) {
        setRecords((current) =>
          current.map((record) => (record.id === id ? refreshed.data! : record)),
        )
      } else {
        setRecords((current) =>
          current.map((record) =>
            record.id === id ? { ...record, active } : record,
          ),
        )
        toast.error(refreshed.message || 'Status changed, but the service could not be refreshed.')
      }
    } catch (mutationError) {
      toast.error(mutationError instanceof Error ? mutationError.message : 'Failed to update service status.')
    } finally {
      setUpdatingIds((current) => {
        const next = new Set(current)
        next.delete(id)
        return next
      })
    }
  }, [records, updatingIds])

  const toggleFleetActive = useCallback(async (id: string) => {
    if (updatingIds.has(id)) return

    const fleet = fleetRecords.find((record) => record.id === id)
    if (!fleet) return

    const active = !fleet.active
    setUpdatingIds((current) => new Set(current).add(id))

    try {
      if (!USE_DASHBOARD_REAL_BACKEND) {
        setFleetRecords((current) =>
          current.map((record) =>
            record.id === id ? { ...record, active } : record,
          ),
        )
        return
      }

      const updated = await apiToggleFleetStatus(id, active)
      if (!updated.success) {
        toast.error(updated.message || 'Failed to update fleet status.')
        return
      }

      const refreshed = await apiGetFleet(id)
      if (refreshed.success && refreshed.data) {
        setFleetRecords((current) =>
          current.map((record) => (record.id === id ? refreshed.data! : record)),
        )
      } else {
        setFleetRecords((current) =>
          current.map((record) =>
            record.id === id ? { ...record, active } : record,
          ),
        )
        toast.error(refreshed.message || 'Status changed, but the fleet could not be refreshed.')
      }
    } catch (mutationError) {
      toast.error(mutationError instanceof Error ? mutationError.message : 'Failed to update fleet status.')
    } finally {
      setUpdatingIds((current) => {
        const next = new Set(current)
        next.delete(id)
        return next
      })
    }
  }, [fleetRecords, updatingIds])

  return {
    records,
    visible,
    category,
    setCategory,
    toggleActive,
    fleetRecords,
    toggleFleetActive,
    updatingIds,
    isLoading,
    error,
  }
}
